import { Injectable } from "@angular/core";
import * as moment from "moment";
import { Observable } from "rxjs";
import { HttpService } from "../http/http.service";
import {
  ChangeEmail,
  ChangeRole, Credit, CreditCharge, CreditHistoryItem, Member, MemberActivityCount,
  MemberPage, MemberTeamPage, Modu, PageInvoiceHistory, Policy, PrintHistoryItem, ResetPassword
} from "./member";

@Injectable()
export class MemberService {
  private url = "/api/members";

  getModuURL(): Observable<Modu> {
    return this.httpService
      .get(`${this.url}/modu/sso`)
      .map((res) => res.json() as Modu);
  }

  getPolicy(): Promise<Policy> {
    return this.httpService
      .get(`${this.url}/privacy_policy`)
      .map((res) => res.json() as Policy)
      .toPromise();
  }

  setPolicy(data: Policy): Promise<Policy> {
    return this.httpService
      .post(`${this.url}/privacy_policy`, data)
      .map((res) => res.json() as Policy)
      .toPromise();
  }

  leave(): Promise<any> {
    return this.httpService.post(`${this.url}/leave`, {}).toPromise();
  }

  // 확인 ___

  getCredit(): Observable<Credit> {
    return this.httpService
      .get(`${this.url}/credit`)
      .map((res) => res.json() as Credit);
  }

  getCreditCharge(): Observable<CreditCharge> {
    return this.httpService
      .get(`${this.url}/credit/charge`)
      .map((res) => res.json() as CreditCharge);
  }

  getCreditHistory(
    page: number,
    startTime: string,
    endTime: string
  ): Observable<CreditHistoryItem[]> {
    return this.httpService
      .get(
        `${this.url}/credit/history?page=${page}&size=10&startTime=${startTime}&endTime=${endTime}`
      )
      .map((res) => res.json() as CreditHistoryItem[]);
  }

  getCreditFile(team: string, location: string, year: number, month: number) {
    return this.httpService.download(
      `${this.url}/credit/excel?location=${location}&year=${year}&month=${month}`
    );
  }

  getInvoiceHistory(
    page: number,
    startTime: string,
    endTime: string
  ): Observable<PageInvoiceHistory> {
    return this.httpService
      .get(
        `${this.url}/invoice/history?page=${page}&size=10&startTime=${startTime}&endTime=${endTime}`
      )
      .map((res) => res.json() as PageInvoiceHistory);
  }

  getInvoiceFile(seq: string, original_nm: string) {
    return this.httpService.download(
      `${this.url}/invoice/${seq}?name=${original_nm}`
    );
  }

  getPrintHistory(
    page: number,
    startTime: string,
    endTime: string
  ): Observable<PrintHistoryItem[]> {
    return this.httpService
      .get(
        `${this.url}/print/history?page=${page}&size=10&startTime=${startTime}&endTime=${endTime}`
      )
      .map((res) => res.json() as PrintHistoryItem[]);
  }

  getHomePage() {
    return this.httpService.get(`${this.url}/mypage`).map((res) => res.json());
  }

  getQRCode(type?: string) {
    return this.httpService
      .get(type === 'sh' ? `${this.url}/me/qrcode/sh` : `${this.url}/me/qrcode`)
      .map((res) => res.json());
  }

  getSHQRCode() {
    return this.httpService
      .get(`${this.url}/me/sh/qrcode`)
      .map((res) => res.json());
  }

  getMyteam(
    page: number,
    search: string,
    enabledOnly: boolean
  ): Observable<MemberPage> {
    return this.httpService
      .get(
        `${this.url}/myteam?page=${page}` +
        (!search ? "" : `&search=${search}`) +
        (enabledOnly ? "&enabled=true" : "")
      )
      .map((res) => res.json() as MemberPage);
  }

  getMyteamCard() {
    return this.httpService
      .get(`${this.url}/teams/cards`)
      .map((res) => res.json());
  }

  lock(email: string): Observable<Member> {
    let emailEncoded = encodeURIComponent(email);
    return this.httpService
      .put(`${this.url}/lock?email=${emailEncoded}`, {
        fixedDate: moment().format("YYYY-MM-DD"),
      })
      .map((res) => res.json() as Member);
  }

  unlock(email: string): Observable<Member> {
    let emailEncoded = encodeURIComponent(email);
    return this.httpService
      .put(`${this.url}/unlock?email=${emailEncoded}`, {
        fixedDate: moment().format("YYYY-MM-DD"),
      })
      .map((res) => res.json() as Member);
  }

  createMember(body): Observable<Member> {
    return this.httpService
      .post(`${this.url}/create`, body)
      .map((res) => res.json() as Member);
  }

  updateMember(id, body): Observable<Member> {
    return this.httpService
      .post(`${this.url}/teams/member/${id}`, body)
      .map((res) => res.json() as Member);
  }

  constructor(private httpService: HttpService) { }

  // Manangement page
  getMembers(page: number, team: number): Observable<MemberPage> {
    return this.httpService
      .get(this.url + `?page=${page}` + (!team ? "" : `&team=${team}`))
      .map((res) => res.json() as MemberPage);
  }

  // members page
  get(
    page: number,
    search: string,
    sort: string,
    order: string
  ): Observable<MemberPage> {
    return this.httpService
      .get(
        this.url +
        `/similar?page=${page}&size=12` +
        (!search ? "" : `&search=${search}`) +
        (!sort ? "" : `&sort=${sort}`) +
        (!order ? "" : `&direction=${order}`)
      )
      .map((res) => res.json() as MemberPage);
  }
  getTeams(
    page: number,
    location: string,
    search: string
  ): Observable<MemberTeamPage> {
    return this.httpService
      .get(
        "/api/v2/members/teams/list" +
        `?page=${page}&size=12` +
        `${location ? `&location=${location}` : ""}` +
        `${search ? `&search=${search}` : ""}`
      )
      .map((res) => res.json() as MemberTeamPage);
  }

  updateMe(member: Member): Observable<Member> {
    return this.httpService
      .put(`${this.url}/me`, member)
      .map((res) => res.json() as Member);
  }

  agree(): Observable<Member> {
    return this.httpService
      .put(`${this.url}/me/agree`, null)
      .map((res) => res.json() as Member);
  }

  searchTerm(term: string): Observable<Member[]> {
    let urlEncoded = encodeURIComponent(term);
    return this.httpService
      .get(this.url + `?search=${urlEncoded}`)
      .map((res) => res.json() as MemberPage)
      .map((memberPage) => memberPage.content);
  }

  getMyActivityCount(): Observable<MemberActivityCount> {
    return this.httpService
      .get(`${this.url}/me/activity-count`)
      .map((res) => res.json() as MemberActivityCount);
  }

  resetPassword(resetPassword: ResetPassword): Observable<Member> {
    return this.httpService
      .put(`${this.url}/reset-password`, resetPassword)
      .map((res) => res.json() as Member);
  }

  findPassword(email: string): Observable<any> {
    return this.httpService.get(`${this.url}/reset-password?email=${email}`);
  }

  changeEmail(changeEmail: ChangeEmail): Observable<Member> {
    return this.httpService
      .put(`${this.url}/change-email`, changeEmail)
      .map((res) => res.json() as Member);
  }

  changeRole(changeRole: ChangeRole): Observable<Member> {
    return this.httpService
      .put(`${this.url}/change-role`, changeRole)
      .map((res) => res.json() as Member);
  }

  getProfile(email: string): Observable<Member> {
    return this.httpService
      .get(`${this.url}/profile?email=${email}`)
      .map((res) => res.json() as Member);
  }

  report(email: string): Observable<boolean> {
    return this.httpService
      .post(`${this.url}/report?email=${email}`, null)
      .map(res => res.json() as boolean);
  }
}



// WEBPACK FOOTER //
// ./src/app/service/member/member.service.ts